import React, { useState } from 'react';
import AiButton from './AiButton';
import HelpHint from '../help/HelpHint';
import { aiAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { refreshAiStatus } from '../../hooks/useAiStatus';
import type { AiExtractedPlayer } from '../../types/ai';

interface AiExtractPlayerPanelProps {
  onExtracted: (data: AiExtractedPlayer) => void;
}

/**
 * Új játékos felvételénél: egy kitöltött jelentkezési lap fotója vagy beillesztett
 * szövege alapján az AI kitölti az űrlapot. Csak akkor jelenik meg, ha az admin
 * a Rendszerbeállításokban engedélyezte (a dokumentum személyes adatot tartalmaz).
 */
const AiExtractPlayerPanel: React.FC<AiExtractPlayerPanelProps> = ({ onExtracted }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const run = async () => {
    if (!file && !text.trim()) {
      setError('Válassz egy képet, vagy illeszd be a szöveget.');
      return;
    }
    setLoading(true);
    setError(null);
    setWarnings([]);
    try {
      const data = await aiAPI.extractPlayer(file, text);
      onExtracted(data);
      setWarnings(data.warnings);
    } catch (err) {
      setError(getErrorMessage(err, 'Az AI most nem tudta kiolvasni az adatokat.'));
    } finally {
      setLoading(false);
      refreshAiStatus();
    }
  };

  if (!open) {
    return (
      <div className="mb-6 flex items-center gap-1">
        <AiButton onClick={() => setOpen(true)}>Kitöltés dokumentumból</AiButton>
        <HelpHint id="players.aiExtract" />
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-3 rounded-lg border border-violet-200 bg-violet-50 p-4">
      <p className="text-sm text-gray-700">
        Fényképezd le vagy szkenneld be a kitöltött jelentkezési lapot, vagy illeszd be egy email szövegét. Az AI
        kitölti belőle az űrlapot - mentés előtt minden mezőt ellenőrizz.
      </p>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-2 file:text-sm file:font-medium file:text-violet-700"
        aria-label="A jelentkezési lap képe"
      />
      <textarea
        rows={3}
        maxLength={8000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="…vagy illeszd be ide a szöveget"
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="A jelentkezés szövege"
      />
      <p className="text-xs text-gray-500">
        A dokumentum a klub által választott AI szolgáltatóhoz kerül feldolgozásra.
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          Bezárás
        </button>
        <AiButton variant="solid" onClick={run} loading={loading} loadingText="Olvasom…">
          Adatok kiolvasása
        </AiButton>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {warnings.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AiExtractPlayerPanel;
